import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  writeBatch,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  ClassMember,
  ClassMemberPoints,
} from '../types/alarm';

/**
 * ClassMembers 컬렉션 기반 멤버 관리 서비스
 * 모든 멤버 관련 데이터를 ClassMembers 하위에서 체계적으로 관리
 */
export class ClassMembersService {
  
  // ===== 기본 멤버 관리 =====
  
  /**
   * 멤버 초기화 (ClassMembers 구조)
   */
  async initializeMembers(
    userId: string, 
    classId: string, 
    members: ClassMember[]
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      for (const member of members) {
        // 1. 기본 멤버 정보 저장
        const memberRef = doc(db, 'users', userId, 'classes', classId, 'members', member.id, member.id);
        batch.set(memberRef, {
          id: member.id,
          number: member.number,
          name: member.name,
          accessCode: member.accessCode,
          notes: member.notes || '',
          createdAt: member.createdAt,
          updatedAt: member.updatedAt || new Date()
        });

        // 2. 포인트 총점 관리 초기화
        const memberPointsRef = doc(db, 'users', userId, 'classes', classId, 'membersPoints', member.id);
        batch.set(memberPointsRef, {
          studentId: member.id,
          studentName: member.name,
          totalPoints: 0,
          earnedPoints: 0,
          bonusPoints: 0,
          pointHistory: [],
          lastUpdated: new Date(),
          classId
        });

        // 3. 과제별 포인트 상세 초기화
        const memberAssignmentPointsRef = doc(db, 'users', userId, 'classes', classId, 'membersAssignmentPoints', member.id, member.id);
        batch.set(memberAssignmentPointsRef, {
          studentId: member.id,
          assignmentPoints: {},
          lastUpdated: new Date(),
          classId
        });

        // 4. 과제별 메모 초기화
        const memberAssignmentMemoRef = doc(db, 'users', userId, 'classes', classId, 'membersAssignmentMemo', member.id, member.id);
        batch.set(memberAssignmentMemoRef, {
          studentId: member.id,
          assignmentMemos: {},
          lastUpdated: new Date(),
          classId
        });

        // 5. 날짜별 메모 초기화
        const memberMemoRef = doc(db, 'users', userId, 'classes', classId, 'membersMemo', member.id, member.id);
        batch.set(memberMemoRef, {
          studentId: member.id,
          dateMemos: {},
          lastUpdated: new Date(),
          classId
        });
      }
      
      await batch.commit();
      console.log(`✅ ClassMembers 구조로 멤버 초기화 완료: ${members.length}명`);
    } catch (error) {
      console.error('❌ ClassMembers 멤버 초기화 실패:', error);
      throw error;
    }
  }

  /**
   * 기본 멤버 정보 조회
   */
  async getMembers(userId: string, classId: string): Promise<ClassMember[]> {
    try {
      const membersRef = collection(db, 'users', userId, 'classes', classId, 'members');
      const membersSnap = await getDocs(membersRef);
      
      return membersSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as ClassMember[];
    } catch (error) {
      console.error('❌ 멤버 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 멤버 추가
   */
  async addMember(userId: string, classId: string, member: ClassMember): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // 1. 기본 멤버 정보 저장
      const memberRef = doc(db, 'users', userId, 'classes', classId, 'members', member.id, member.id);
      batch.set(memberRef, {
        id: member.id,
        number: member.number,
        name: member.name,
        accessCode: member.accessCode,
        notes: member.notes || '',
        createdAt: member.createdAt,
        updatedAt: member.updatedAt || new Date()
      });

      // 2. 포인트 총점 관리 초기화
      const memberPointsRef = doc(db, 'users', userId, 'classes', classId, 'membersPoints', member.id);
      batch.set(memberPointsRef, {
        studentId: member.id,
        studentName: member.name,
        totalPoints: 0,
        earnedPoints: 0,
        bonusPoints: 0,
        pointHistory: [],
        lastUpdated: new Date(),
        classId
      });

      // 3. 과제별 포인트 상세 초기화
      const memberAssignmentPointsRef = doc(db, 'users', userId, 'classes', classId, 'membersAssignmentPoints', member.id, member.id);
      batch.set(memberAssignmentPointsRef, {
        studentId: member.id,
        assignmentPoints: {},
        lastUpdated: new Date(),
        classId
      });

      // 4. 과제별 메모 초기화
      const memberAssignmentMemoRef = doc(db, 'users', userId, 'classes', classId, 'membersAssignmentMemo', member.id, member.id);
      batch.set(memberAssignmentMemoRef, {
        studentId: member.id,
        assignmentMemos: {},
        lastUpdated: new Date(),
        classId
      });

      // 5. 날짜별 메모 초기화
      const memberMemoRef = doc(db, 'users', userId, 'classes', classId, 'membersMemo', member.id, member.id);
      batch.set(memberMemoRef, {
        studentId: member.id,
        dateMemos: {},
        lastUpdated: new Date(),
        classId
      });
      
      await batch.commit();
      console.log(`✅ 멤버 추가 완료: ${member.name}`);
    } catch (error) {
      console.error('❌ 멤버 추가 실패:', error);
      throw error;
    }
  }

  /**
   * 멤버 삭제
   */
  async deleteMember(userId: string, classId: string, studentId: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // 모든 하위 컬렉션의 문서 삭제
      const collections = ['members', 'membersPoints', 'membersAssignmentPoints', 'membersAssignmentMemo', 'membersMemo'];
      
      for (const collectionName of collections) {
        const docRef = doc(db, 'users', userId, 'classes', classId, collectionName, studentId);
        batch.delete(docRef);
      }
      
      await batch.commit();
      console.log(`✅ 멤버 삭제 완료: ${studentId}`);
    } catch (error) {
      console.error('❌ 멤버 삭제 실패:', error);
      throw error;
    }
  }

  // ===== 포인트 관리 =====
  
  /**
   * 과제 포인트 동기화
   */
  async syncAssignmentPoints(
    userId: string,
    classId: string,
    assignmentId: string,
    assignmentTitle: string,
    assignmentType: 'general' | 'project' | 'routine',
    studentId: string,
    studentName: string,
    newEarnedPoints: number,
    oldEarnedPoints: number
  ): Promise<void> {
    try {
      const pointsDifference = newEarnedPoints - oldEarnedPoints;

      // 1. membersPoints 하위 컬렉션 업데이트
      const memberPointsRef = doc(db, 'users', userId, 'classes', classId, 'membersPoints', studentId);
      const memberPointsDoc = await getDoc(memberPointsRef);

      if (memberPointsDoc.exists()) {
        const memberPointsData = memberPointsDoc.data() as ClassMemberPoints;

        // 레벨업 감지를 위해 이전 총 포인트 저장
        const oldTotalPoints = memberPointsData.totalPoints || 0;

        const newHistoryEntry = {
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'assignment' as const,
          assignmentId,
          points: pointsDifference,
          description: `${assignmentTitle} 과제 포인트 변경`,
          timestamp: new Date(),
          assignmentTitle
        };

        const newTotalPoints = oldTotalPoints + pointsDifference;

        await updateDoc(memberPointsRef, {
          totalPoints: newTotalPoints,
          earnedPoints: memberPointsData.earnedPoints + pointsDifference,
          pointHistory: arrayUnion(newHistoryEntry),
          lastUpdated: new Date()
        });
        
        // 레벨 변화 감지 및 currentLevel 업데이트 (보상은 포인트 조회 페이지에서만 지급)
        try {
          const { PointTypeManagementService } = await import('./pointTypeManagementService');
          const { PointManagementService } = await import('./pointManagementService');
          const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
          
          // MembersPoint 문서에서 포인트 관리 페이지에서 지급한 포인트 합계 가져오기
          const pointManagementService = new PointManagementService(userId, classId);
          const studentPointHistory = await pointManagementService.getStudentPointHistory(studentId);
          const rewardPoints = studentPointHistory.reduce((sum, point) => sum + (point.pointValue || 0), 0);
          
          // 과제 포인트는 membersPoints 컬렉션의 earnedPoints(총 과제 포인트 합계)를 사용
          // 실제 totalPoints = rewardPoints (MembersPoint) + earnedPoints (과제 포인트)
          const actualOldTotalPoints = rewardPoints + (memberPointsData.earnedPoints || 0);
          const actualNewTotalPoints = rewardPoints + ((memberPointsData.earnedPoints || 0) + pointsDifference);
          
          const pointTypeService = new PointTypeManagementService(userId, classId);
          const pointTypeData = await pointTypeService.getPointTypeData();
          const levelUpPoints = pointTypeData.levelUpPoints || 50;
          
          // 레벨 계산
          const calcLevel = (totalPoints: number) => Math.floor((totalPoints || 0) / levelUpPoints);
          const oldLevel = calcLevel(actualOldTotalPoints);
          const newLevel = calcLevel(actualNewTotalPoints);
          
          // 레벨 변화가 있으면 currentLevel만 업데이트 (보상은 포인트 조회 페이지에서 처리)
          if (oldLevel !== newLevel) {
            const partitionService = new ClassMembersPartitionService(userId, classId);
            await partitionService.updateStudentCurrentLevel(studentId, newLevel);
            console.log(`✅ 레벨 변화 감지 (보상은 포인트 조회 페이지에서 처리): ${studentId}, 레벨 ${oldLevel} → ${newLevel}`);
          }
        } catch (error) {
          console.error(`레벨 변화 감지 실패 (멤버 ${studentId}):`, error);
          // 레벨 감지 실패해도 포인트 업데이트는 성공한 상태이므로 에러를 다시 던지지 않음
        }
      }

      // 2. membersAssignmentPoints 하위 컬렉션에 과제별 포인트 저장
      const memberAssignmentPointsRef = doc(db, 'users', userId, 'classes', classId, 'membersAssignmentPoints', studentId);
      const memberAssignmentPointsDoc = await getDoc(memberAssignmentPointsRef);
      const currentAssignmentPoints = memberAssignmentPointsDoc.exists()
        ? (memberAssignmentPointsDoc.data().assignmentPoints || {})
        : {};

      const updatedAssignmentPoints = {
        ...currentAssignmentPoints,
        [assignmentId]: {
          points: newEarnedPoints,
          assignmentTitle,
          assignmentType,
          lastUpdated: new Date()
        }
      };

      await setDoc(memberAssignmentPointsRef, {
        studentId,
        assignmentPoints: updatedAssignmentPoints,
        lastUpdated: new Date(),
        classId
      });

      console.log(`✅ ClassMembers 포인트 동기화 완료: ${studentName} (+${pointsDifference}점)`);
    } catch (error) {
      console.error('❌ ClassMembers 포인트 동기화 실패:', error);
      throw error;
    }
  }

  /**
   * 보너스 포인트 추가
   */
  async addBonusPoints(
    userId: string,
    classId: string,
    studentId: string,
    points: number,
    description: string
  ): Promise<void> {
    try {
      const memberPointsRef = doc(db, 'users', userId, 'classes', classId, 'membersPoints', studentId);
      const memberPointsDoc = await getDoc(memberPointsRef);

      if (memberPointsDoc.exists()) {
        const memberPointsData = memberPointsDoc.data() as ClassMemberPoints;

        const bonusHistoryEntry = {
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'bonus' as const,
          points,
          description,
          timestamp: new Date()
        };

        await updateDoc(memberPointsRef, {
          totalPoints: memberPointsData.totalPoints + points,
          bonusPoints: memberPointsData.bonusPoints + points,
          pointHistory: arrayUnion(bonusHistoryEntry),
          lastUpdated: new Date()
        });

        console.log(`✅ 보너스 포인트 추가 완료: ${studentId} (+${points}점)`);
      }
    } catch (error) {
      console.error('❌ 보너스 포인트 추가 실패:', error);
      throw error;
    }
  }

  /**
   * 과제 포인트 제거
   */
  async removeAssignmentPoints(
    userId: string,
    classId: string,
    assignmentId: string,
    assignmentTitle: string
  ): Promise<void> {
    try {
      const membersRef = collection(db, 'users', userId, 'classes', classId, 'membersPoints');
      const membersSnap = await getDocs(membersRef);

      for (const memberDoc of membersSnap.docs) {
        const memberPointsData = memberDoc.data() as ClassMemberPoints;
        
        // 해당 과제의 포인트 히스토리 찾기
        const assignmentHistory = memberPointsData.pointHistory.filter(entry => entry.assignmentId === assignmentId);
        
        if (assignmentHistory.length > 0) {
          const totalPointsToRemove = assignmentHistory.reduce((sum, entry) => sum + entry.points, 0);
          
          // 레벨업 감지를 위해 이전 총 포인트 저장
          const oldTotalPoints = memberPointsData.totalPoints || 0;
          const newTotalPoints = oldTotalPoints - totalPointsToRemove;
          
          // 포인트 히스토리에서 해당 과제 제거
          const updatedHistory = memberPointsData.pointHistory.filter(entry => entry.assignmentId !== assignmentId);
          
          await updateDoc(memberDoc.ref, {
            totalPoints: newTotalPoints,
            earnedPoints: memberPointsData.earnedPoints - totalPointsToRemove,
            pointHistory: updatedHistory,
            lastUpdated: new Date()
          });
          
          // 레벨업 감지 및 보상 처리
          try {
            const { PointTypeManagementService } = await import('./pointTypeManagementService');
            const { PointManagementService } = await import('./pointManagementService');
            const { syncLevelUpRewardsByPoints } = await import('../utils/levelUpRewardHelper');
            
            // MembersPoint 문서에서 포인트 관리 페이지에서 지급한 포인트 합계 가져오기
            const pointManagementService = new PointManagementService(userId, classId);
            const studentPointHistory = await pointManagementService.getStudentPointHistory(memberDoc.id);
            const rewardPoints = studentPointHistory.reduce((sum, point) => sum + (point.pointValue || 0), 0);
            
            // 실제 totalPoints = rewardPoints (MembersPoint) + earnedPoints (과제 포인트)
            const actualOldTotalPoints = rewardPoints + memberPointsData.earnedPoints;
            const actualNewTotalPoints = rewardPoints + (memberPointsData.earnedPoints - totalPointsToRemove);
            
            const pointTypeService = new PointTypeManagementService(userId, classId);
            const pointTypeData = await pointTypeService.getPointTypeData();
            const levelUpPoints = pointTypeData.levelUpPoints || 50;
            
            await syncLevelUpRewardsByPoints(
              userId,
              classId,
              memberDoc.id,
              actualOldTotalPoints,
              actualNewTotalPoints,
              levelUpPoints
            );
          } catch (error) {
            console.error(`레벨업 보상 처리 실패 (멤버 ${memberDoc.id}):`, error);
            // 레벨업 보상 실패해도 포인트 업데이트는 성공한 상태이므로 에러를 다시 던지지 않음
          }
        }

        // 과제별 포인트 상세에서도 제거
        const memberAssignmentPointsRef = doc(db, 'users', userId, 'classes', classId, 'membersAssignmentPoints', memberDoc.id, memberDoc.id);
        const memberAssignmentPointsDoc = await getDoc(memberAssignmentPointsRef);
        
        if (memberAssignmentPointsDoc.exists()) {
          const currentAssignmentPoints = memberAssignmentPointsDoc.data().assignmentPoints || {};
          const { [assignmentId]: removed, ...updatedAssignmentPoints } = currentAssignmentPoints;
          
          await setDoc(memberAssignmentPointsRef, {
            studentId: memberDoc.id,
            assignmentPoints: updatedAssignmentPoints,
            lastUpdated: new Date(),
            classId
          });
        }
      }

      console.log(`✅ 과제 포인트 제거 완료: ${assignmentTitle}`);
    } catch (error) {
      console.error('❌ 과제 포인트 제거 실패:', error);
      throw error;
    }
  }

  // ===== 메모 관리 =====
  
  /**
   * 과제 메모 저장
   */
  async saveAssignmentMemo(
    userId: string,
    classId: string,
    studentId: string,
    assignmentId: string,
    assignmentTitle: string,
    assignmentType: 'general' | 'project' | 'routine',
    memo: string,
    notes: string
  ): Promise<void> {
    try {
      const memberAssignmentMemoRef = doc(db, 'users', userId, 'classes', classId, 'membersAssignmentMemo', studentId);
      const memberAssignmentMemoDoc = await getDoc(memberAssignmentMemoRef);
      
      const currentAssignmentMemos = memberAssignmentMemoDoc.exists()
        ? (memberAssignmentMemoDoc.data().assignmentMemos || {})
        : {};

      const updatedAssignmentMemos = {
        ...currentAssignmentMemos,
        [assignmentId]: {
          assignmentTitle,
          assignmentType,
          memo,
          notes,
          lastUpdated: new Date()
        }
      };

      await setDoc(memberAssignmentMemoRef, {
        studentId,
        assignmentMemos: updatedAssignmentMemos,
        lastUpdated: new Date(),
        classId
      });

      console.log(`✅ 과제 메모 저장 완료: ${studentId} - ${assignmentTitle}`);
    } catch (error) {
      console.error('❌ 과제 메모 저장 실패:', error);
      throw error;
    }
  }

  /**
   * 날짜 메모 저장
   */
  async saveDateMemo(
    userId: string,
    classId: string,
    studentId: string,
    dateId: string,
    memo: string,
    notes: string
  ): Promise<void> {
    try {
      const memberMemoRef = doc(db, 'users', userId, 'classes', classId, 'membersMemo', studentId);
      const memberMemoDoc = await getDoc(memberMemoRef);
      
      const currentDateMemos = memberMemoDoc.exists()
        ? (memberMemoDoc.data().dateMemos || {})
        : {};

      const updatedDateMemos = {
        ...currentDateMemos,
        [dateId]: {
          memo,
          notes,
          createdAt: new Date()
        }
      };

      await setDoc(memberMemoRef, {
        studentId,
        dateMemos: updatedDateMemos,
        lastUpdated: new Date(),
        classId
      });

      console.log(`✅ 날짜 메모 저장 완료: ${studentId} - ${dateId}`);
    } catch (error) {
      console.error('❌ 날짜 메모 저장 실패:', error);
      throw error;
    }
  }

  // ===== 조회 메서드 =====
  
  /**
   * 멤버 포인트 조회
   */
  async getMemberPoints(userId: string, classId: string, studentId: string): Promise<ClassMemberPoints | null> {
    try {
      const memberPointsRef = doc(db, 'users', userId, 'classes', classId, 'membersPoints', studentId);
      const memberPointsDoc = await getDoc(memberPointsRef);
      
      if (memberPointsDoc.exists()) {
        const data = memberPointsDoc.data();
        return {
          studentId: data.studentId,
          studentName: data.studentName,
          totalPoints: data.totalPoints,
          earnedPoints: data.earnedPoints,
          bonusPoints: data.bonusPoints,
          pointHistory: data.pointHistory?.map((entry: any) => ({
            ...entry,
            timestamp: entry.timestamp?.toDate() || new Date()
          })) || [],
          lastUpdated: data.lastUpdated?.toDate() || new Date(),
          classId: data.classId
        } as ClassMemberPoints;
      }
      
      return null;
    } catch (error) {
      console.error('❌ 멤버 포인트 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 모든 멤버 포인트 조회
   */
  async getAllMemberPoints(userId: string, classId: string): Promise<ClassMemberPoints[]> {
    try {
      const membersPointsRef = collection(db, 'users', userId, 'classes', classId, 'membersPoints');
      const membersPointsSnap = await getDocs(membersPointsRef);
      
      return membersPointsSnap.docs.map(doc => {
        const data = doc.data();
        return {
          studentId: data.studentId,
          studentName: data.studentName,
          totalPoints: data.totalPoints,
          earnedPoints: data.earnedPoints,
          bonusPoints: data.bonusPoints,
          pointHistory: data.pointHistory?.map((entry: any) => ({
            ...entry,
            timestamp: entry.timestamp?.toDate() || new Date()
          })) || [],
          lastUpdated: data.lastUpdated?.toDate() || new Date(),
          classId: data.classId
        } as ClassMemberPoints;
      });
    } catch (error) {
      console.error('❌ 모든 멤버 포인트 조회 실패:', error);
      throw error;
    }
  }
}

export const classMembersService = new ClassMembersService();
